import React, { useEffect, useState } from "react";
import { Country, State, City } from 'country-state-city';
import axios from "axios";
import { API_URL } from "../../utils/constant";
import { FileSpreadsheet } from "lucide-react";
import GstReturnModal from "../../components/GstReturnModal";


const LabeledInputWithCheckbox = ({
    label,
    name,
    type = "text",
    value,
    onChange,
    show,
    className = "col-md-6 mb-3"
}) => (
    <div className={`${className}`}>
        <label className="form-label">{label}</label>
        <div className="input-group">
            {name === "footerStatement" ? (
                <textarea
                    name={name}
                    className="form-control"
                    value={value}
                    onChange={onChange}
                    rows={3}
                    placeholder="Enter footer statement for POS bill (paragraph supported)"
                />
            ) : (
                <input
                    type={type}
                    name={name}
                    className="form-control"
                    value={value}
                    onChange={onChange}
                    disabled={name === 'email'}
                    required={(name != 'website' && type === "text") || type === "email"}
                />
            )}
            <div className="input-group-text">
                <input
                    type="checkbox"
                    name={name}
                    data-group="showOnBill"
                    className="form-check-input mt-0"
                    checked={show}
                    onChange={onChange}
                    id={`show-${name}`}
                    title="Show in POS Bill"
                />
            </div>
        </div>
        <small className="text-muted">Show in POS Bill</small>
    </div>
);


const CompanyProfile = () => {

    const company = JSON.parse(localStorage.getItem("company")) || {};
    const [formData, setFormData] = useState({
        companyName: "",
        tradeName: "",
        incorporationDate: "",
        companyType: "",
        contact: "",
        email: "",
        country: "",
        state: "",
        city: "",
        address: "",
        pincode: "",
        gstNo: "",
        website: "",
        pan: "",
        posLogo: null,
        company_logo:  null,
        footerStatement: "",
        showOnBill: {
            companyName: true,
            tradeName: true,
            gstNo: true,
            address: true,
            contact: true,
            website: false,
            pan: false,
            footerStatement: true,
        }
    });

    const [pendingState, setPendingState] = useState(null);
    const [pendingCity, setPendingCity] = useState(null);
    const [posImg, setPosImg] = useState(null);
    const [companyLogo, setCompanyLogo] = useState(null);
    const [companyData, setCompanyData] = useState({});
    const [showGstModal, setShowGstModal] = useState(false);
    // Country/State/City dropdowns
    const [countryList, setCountryList] = useState([]);
    const [stateList, setStateList] = useState([]);
    const [cityList, setCityList] = useState([]);

    // Populate country list on mount
    useEffect(() => {
        setCountryList(Country.getAllCountries());
    }, []);

    // Populate state list when country changes
    useEffect(() => {
        if (formData.country) {
            const selectedCountry = countryList.find(c => c.name === formData.country);
            if (selectedCountry) {
                setStateList(State.getStatesOfCountry(selectedCountry.isoCode));
            } else {
                setStateList([]);
            }
            setFormData(prev => ({ ...prev, state: '', city: '' }));
        }
        setPendingState(companyData.state);
        setPendingCity(companyData.city);
    }, [formData.country, countryList]);

    // Populate city list when state changes
    useEffect(() => {
        if (formData.state && formData.country) {
            const selectedCountry = countryList.find(c => c.name === formData.country);
            const selectedState = stateList.find(s => s.name === formData.state);
            if (selectedCountry && selectedState) {
                setCityList(City.getCitiesOfState(selectedCountry.isoCode, selectedState.isoCode));
            } else {
                setCityList([]);
            }
            setFormData(prev => ({ ...prev, city: '' }));
        }
    }, [formData.state, formData.country, stateList, countryList]);

    const fetchCompany = async () => {
        if (!company.company?.id) return;
        try {
            const res = await axios.get(`${API_URL}/companies/${company.company?.id}`);
            const data = res.data;
            setCompanyData(data);
            setFormData(prev => {
                
                return {
                    ...prev,
                    companyName: data.company_name || prev.companyName,
                    tradeName: data.trade || prev.tradeName,
                    incorporationDate: data.doi || prev.incorporationDate,
                    companyType: data.company_type || prev.companyType,
                    contact: data.mobile_no || prev.contact,
                    email: data.email || prev.email,
                    country: data.country,
                    address: data.register_address || prev.address,
                    pincode: data.pincode || prev.pincode,
                    gstNo: data.gst_number || prev.gstNo,
                    website: data.website || prev.website,
                    pan: data.company_pan_number || prev.pan,
                    footerStatement: data.footer_statement || prev.footerStatement,
                    showOnBill: JSON.parse(data.showOnBill) || prev.showOnBill,
                };
            });
            setPosImg(data.posLogo);
            setCompanyLogo(data.company_logo);
        } catch (err) {
            console.error("Failed to fetch company:", err);
        }
    };
    
    useEffect(() => {
        
        fetchCompany();
    }, [company.company?.id]);

     useEffect(() => {
        if (pendingState && stateList.length > 0) {
            const found = stateList.find(s => s.name === pendingState);
            if (found) {
                setFormData(prev => ({ ...prev, state: pendingState }));
                setPendingState(null);
            }
        }
    }, [pendingState, stateList]);

    // Set city from pendingCity after cityList is loaded
    useEffect(() => {
        if (pendingCity && cityList.length > 0) {
            const found = cityList.find(c => c.name === pendingCity);
            if (found) {
                setFormData(prev => ({ ...prev, city: pendingCity }));
                setPendingCity(null);
            }
        }
    }, [pendingCity, cityList]);

    const handleChange = (e) => {
        const { name, value, type, checked, files, dataset } = e.target;

        if (dataset.group === "showOnBill") {
            setFormData({
                ...formData,
                showOnBill: {
                    ...formData.showOnBill,
                    [name]: checked,
                },
            });
        } else if (type === "checkbox") {
            setFormData({ ...formData, [name]: checked });
        } else if (type === "file") {
            setFormData({ ...formData, [name]: files[0] });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!company.company?.id) return;
        const payload = new FormData();
        Object.keys(formData).forEach((key) => {
            if(key != "showOnBill"){
                payload.append(key, formData[key]);
            }
        });
        payload.append("showOnBill", JSON.stringify(formData.showOnBill));


        try {
            const res = await axios.put(`${API_URL}/companies/${company.company?.id}`, payload, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            alert("Company profile submitted successfully!");
        } catch (err) {
            console.error("Submission failed:", err);
            alert("Error submitting company profile.");
        }
    };

    return (
        <div className="row mt-4">
            <div className="col-md-12 mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h2 className="mb-0">Company Profile</h2>
                <button
                    type="button"
                    className="btn btn-success d-flex align-items-center fw-bold shadow-sm px-3 py-2 rounded-3"
                    onClick={() => setShowGstModal(true)}
                    style={{ background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)', border: 'none' }}
                >
                    <FileSpreadsheet size={18} className="me-2" />
                    Download GST Return (Excel)
                </button>
            </div>
            <div className="col-md-12">
                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit} encType="multipart/form-data">
                            <div className="row">
                                {companyLogo && (
                                    <div className="col-md-2">
                                        <img src={`${API_URL}${companyLogo}`} alt="Company Logo" className="img-thumbnail mb-3" style={{ maxWidth: '100%' }}/>
                                    </div>
                                )}
                                <div className={`${companyLogo?'col-md-4':'col-md-6 '} mb-3`}>
                                    <label>Company Logo</label>
                                    <input type="file" name="company_logo" className="form-control" onChange={handleChange} accept="image/*" />
                                </div>
                                {/* Only use LabeledInputWithCheckbox for fields in formData.showOnBill */}
                                <LabeledInputWithCheckbox
                                    label="Company Name"
                                    name="companyName"
                                    value={formData.companyName}
                                    onChange={handleChange}
                                    show={formData.showOnBill.companyName}
                                />
                                <LabeledInputWithCheckbox
                                    label="Trade Name"
                                    name="tradeName"
                                    value={formData.tradeName}
                                    onChange={handleChange}
                                    show={formData.showOnBill.tradeName}
                                />
                                <LabeledInputWithCheckbox
                                    label="GST No"
                                    name="gstNo"
                                    value={formData.gstNo}
                                    onChange={handleChange}
                                    show={formData.showOnBill.gstNo}
                                />
                                <LabeledInputWithCheckbox
                                    label="Address"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    show={formData.showOnBill.address}
                                />
                                <LabeledInputWithCheckbox
                                    label="Contact"
                                    name="contact"
                                    value={formData.contact}
                                    onChange={handleChange}
                                    show={formData.showOnBill.contact}
                                />
                                <LabeledInputWithCheckbox
                                    label="Website Link"
                                    name="website"
                                    value={formData.website}
                                    onChange={handleChange}
                                    show={formData.showOnBill.website}
                                />
                                <LabeledInputWithCheckbox
                                    label="PAN"
                                    name="pan"
                                    value={formData.pan}
                                    onChange={handleChange}
                                    show={formData.showOnBill.pan}
                                />
                               

                                {/* Other fields use regular inputs */}
                                <div className="col-md-6 mb-3">
                                    <label>Incorporation Date</label>
                                    <input type="date" name="incorporationDate" className="form-control" value={formData.incorporationDate} onChange={handleChange} />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label>Company Type</label>
                                    <input type="text" name="companyType" className="form-control" value={formData.companyType} onChange={handleChange} />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label>Email</label>
                                    <input type="email" name="email" readOnly disabled className="form-control" value={formData.email} onChange={handleChange} />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label>Country</label>
                                    <select
                                        name="country"
                                        className="form-control"
                                        value={formData.country}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select Country</option>
                                        {countryList.map((country) => (
                                            <option key={country.isoCode} value={country.name}>{country.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label>State</label>
                                    <select
                                        name="state"
                                        className="form-control"
                                        value={formData.state}
                                        onChange={handleChange}
                                        disabled={!formData.country}
                                    >
                                        <option value="">Select State</option>
                                        {stateList.map((state) => (
                                            <option key={state.isoCode} value={state.name}>{state.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label>City</label>
                                    <select
                                        name="city"
                                        className="form-control"
                                        value={formData.city}
                                        onChange={handleChange}
                                        disabled={!formData.state}
                                    >
                                        <option value="">Select City</option>
                                        {cityList.map((city) => (
                                            <option key={city.name} value={city.name}>{city.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label>Pincode</label>
                                    <input type="text" name="pincode" className="form-control" value={formData.pincode} onChange={handleChange} />
                                </div>
                                {posImg && (
                                    <div className="col-md-2">
                                        <img src={`${API_URL}${posImg}`} alt="POS Logo" className="img-thumbnail mb-3" style={{ maxWidth: '100%' }}/>
                                    </div>
                                )}
                                <div className={`${posImg?'col-md-4':'col-md-6'} mb-3`}>
                                    <label>Logo for POS Billing</label>
                                    <input type="file" name="posLogo" className="form-control" onChange={handleChange} accept="image/*" />
                                </div>

                                 <LabeledInputWithCheckbox
                                    label="POS Footer Statement"
                                    name="footerStatement"
                                    value={formData.footerStatement}
                                    onChange={handleChange}
                                    show={formData.showOnBill.footerStatement}
                                    className="col-md-12 mb-3 h-30"
                                />
                                <div className="col-md-12 text-end">
                                    <button type="submit" className="btn btn-primary">Submit</button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <GstReturnModal isOpen={showGstModal} onClose={() => setShowGstModal(false)} />
        </div>
    );
};

export default CompanyProfile;
